import "rxjs/add/operator/map";
import { ajax } from "lib/observable";
import { Observable } from "rxjs";
import {
    WHITEBOARD_ADD_PERMISSION,
    WHITEBOARD_ADD_PERMISSION_COMPLETED,
    WHITEBOARD_CHANGE_PERMISSION,
    WHITEBOARD_CONTACTS_RESET,
} from "constants/action-types";
import siteConfig from "site-config";
import { formatMessage } from "lib/globalize-util";
import querystring, { ParsedUrlQueryInput } from "querystring";
import { PAGE_LIMIT } from "constants/whiteboard";
import { List } from "immutable";
import { add as snackbarAdd } from "actions/snackbars";

const parseResponse = ({ response }: any) => {
    return response.data[0];
};

const parseCompaniesResponse = ({ response }: any) => {
    let buyers = [];
    if (response.length) {
        response.forEach((company) => {
            buyers = buyers.concat(company.__buyers);
        });
    }
    return buyers;
};
interface ICompanyQuery extends ParsedUrlQueryInput {
    __limit: number;
    "__buyers.__ref"?: string[];
    __fields: string;
}

const fetchCompanies = (buyerIds) => {
    return (action, store) => {
        const query: ICompanyQuery = {
            "__buyers.__ref": buyerIds,
            __fields: "__buyers",
            __limit: PAGE_LIMIT,
        };
        return ajax({
            method: "GET",
            url: `${siteConfig.api}/api/v3.0/companies?${querystring.stringify(query)}`,
        });
    };
};

const updatePermissionsWithUserInfo = ({ boardUsers, userId, newBuyer }) => {
    const boardPermission = boardUsers.find((user) => {
        return user.identity === userId;
    });
    if (boardPermission && newBuyer) {
        boardPermission.name = newBuyer.__name;
        boardPermission.email = newBuyer.__email;
    }
};

const updatePermissionsWithBrandUserInfo = ({ boardUsers, brandUsers }) => {
    boardUsers.forEach((boardUser) => {
        const matchingBrandUser = brandUsers.find((brandUser) => {
            return brandUser.ref === boardUser.identity;
        });

        if (matchingBrandUser) {
            boardUser.name = matchingBrandUser.name;
            boardUser.email = matchingBrandUser.email;
        }
    });
};

export const updatePermissionEpic = (action$, store) => {
    return action$
        .ofType(WHITEBOARD_CHANGE_PERMISSION)
        .map((action) => {
            return action.payload;
        })
        .concatMap(({ board_id, identity, identity_type = "USER", role = "VIEWER" }) =>
            ajax({
                body: {
                    board_id,
                    identity,
                    permissions: [
                        {
                            board_id,
                            identity,
                            identity_type,
                            role,
                        },
                    ],
                },
                method: "PUT",
                url: `${siteConfig.goAPI}/v1/boards/${board_id}/permissions/${identity}`,
            })
                .map(parseResponse)
                .flatMap((data) => {
                    const { entities } = store.getState();
                    const board = entities.boardPermission[board_id];
                    const boardUsers = board.users.toJS();
                    const modifiedUsers = boardUsers.map((user) => {
                        if (user.identity === data.identity) {
                            user.role = role;
                        }

                        return user;
                    });

                    entities.boardPermission[board_id] = entities.boardPermission[board_id].update("users", (users) => {
                        return List(modifiedUsers);
                    });

                    return [
                        {
                            payload: [board_id],
                            type: WHITEBOARD_ADD_PERMISSION_COMPLETED,
                        },
                    ];
                })
                .catch((error) => {
                    let errorMessage = formatMessage("Oops! Something went wrong. Please refresh and try again.");
                    if (error.status === 403) {
                        errorMessage = formatMessage("You do not have permission to edit users on this whiteboard");
                    }
                    return Observable.merge(
                        Observable.of({
                            error: true,
                            payload: new Error(errorMessage),
                            type: WHITEBOARD_ADD_PERMISSION_COMPLETED,
                        }),
                        Observable.of(snackbarAdd({ message: errorMessage })),
                    );
                }),
        );
};

const addPermission = ({ boardId, userId, userType = "USER", permission = "VIEWER" }) => {
    return ajax({
        body: {
            board_id: boardId,
            identity: userId,
            identity_type: userType,
            role: permission,
        },
        method: "POST",
        url: `${siteConfig.goAPI}/v1/boards/${boardId}/permissions`,
    }).map(parseResponse);
};

const addPermissions = ({ boardId, userIds, userType = "USER", permission = "VIEWER" }) => {
    return (action, store) => {
        const queries = userIds.map((id) => {
            return addPermission({ boardId, userId: id, userType, permission });
        });

        return queries;
    };
};

// The add permission event send an array of ids to add
export const addPermissionsEpic = (action$, store) => {
    return action$
        .ofType(WHITEBOARD_ADD_PERMISSION)
        .map((action) => {
            return action.payload;
        })
        .mergeMap(({ boardId, userIds, userType = "USER", permission = "VIEWER" }) => {
            const queries = addPermissions({ boardId, userIds, userType, permission })(action$, store);
            return Observable.forkJoin(queries)
                .flatMap((data) => {
                    const { entities } = store.getState();
                    const newUsers = entities.boardPermission[boardId].users.concat(data);
                    entities.boardPermission[boardId] = entities.boardPermission[boardId].set("users", newUsers);
                    return fetchCompanies(userIds)(action$, store);
                })
                .map(parseCompaniesResponse)
                .flatMap((companies) => {
                    const { entities } = store.getState();
                    const brandUsers = store.getState().scope.brand.modules.base.user_connections;
                    const board = entities.boardPermission[boardId];
                    const boardUsers = board.users.toJS();

                    userIds.forEach((id) => {
                        const newBuyer = companies.find((buyer) => {
                            return buyer.__ref === id;
                        });
                        updatePermissionsWithUserInfo({ boardUsers, userId: id, newBuyer });
                        updatePermissionsWithBrandUserInfo({ boardUsers, brandUsers });
                    });

                    entities.boardPermission[boardId] = entities.boardPermission[boardId].update(
                        "users",
                        (permissions) => {
                            return List(boardUsers);
                        },
                    );
                    return [
                        {
                            payload: [boardId],
                            type: WHITEBOARD_ADD_PERMISSION_COMPLETED,
                        },
                        {
                            payload: userIds,
                            type: WHITEBOARD_CONTACTS_RESET,
                        },
                    ];
                });
        })
        .catch((error) => {
            let errorMessage = formatMessage("Oops! Something went wrong. Please refresh and try again.");
            if (error.status === 401) {
                errorMessage = formatMessage("You do not have permission to add users to this whiteboard");
            }
            return Observable.merge(
                Observable.of({
                    error: true,
                    payload: new Error(errorMessage),
                    type: WHITEBOARD_ADD_PERMISSION_COMPLETED,
                }),
                Observable.of(snackbarAdd({ message: errorMessage })),
            );
        });
};
